import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@prakash-software/shared/design/tokens.css';
import './app.css';
import { configureBus, type BusEvent } from '@prakash-software/shared';
import { configureWebRTCMetrics } from './lib/webrtc-metrics';
import { App } from './App';
import { seedFixtures } from './mock/fixtures';
import { DevPanel } from './mock/dev-panel';
import { ToastDeck } from './components/ToastDeck';
import { ConfirmDialog } from './components/ConfirmDialog';
import { InstallBanner } from './components/InstallBanner';
import { installEventBridge } from './lib/event-bridge';
import { installAgentBusBridge } from './lib/bus-bridge';
// Session 5.8: AI orchestrator moved to the server. Browser keys retired.
// `installAIOrchestrator` is intentionally NOT imported here anymore.
import { listenForInstallPrompt, registerServiceWorker } from './lib/pwa';
import { useAgentStore } from './stores/agent';
import { applyTheme, readTheme } from './lib/theme';

// Apply the persisted/OS theme before first paint so deep links (e.g. straight
// to /dashboard, bypassing the Landing page) are themed and the logo shows.
applyTheme(readTheme());

// Wire the shared event bus to the Socket.IO server. Reads the current JWT
// from the agent store on every (re)connect so token rotation is transparent.
// Honor explicit VITE_AUTH_SERVER_URL when set; otherwise derive from the
// browser's hostname so a teammate visiting http://10.2.2.9:5175 talks to
// http://10.2.2.9:4000 without anyone editing .env.
function resolveServerUrl(): string {
  const explicit = import.meta.env.VITE_AUTH_SERVER_URL as string | undefined;
  if (explicit && explicit !== 'auto') return explicit;
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const { protocol, hostname, port } = window.location;
    // Dev = page on a non-standard port like 5175 → Node server on :4000.
    // Deployed = no port (80/443) → reverse proxy on same origin.
    if (port && port !== '80' && port !== '443') {
      return `${protocol}//${hostname}:4000`;
    }
    return `${protocol}//${hostname}`;
  }
  return 'http://localhost:4000';
}
const SERVER_URL = resolveServerUrl();
// Replies the server hasn't acknowledged survive a reload and are re-sent on
// the next connect; the server dedupes by message id. Events restored under a
// different agent's login are rejected as forbidden and dropped.
const OUTBOX_KEY = 'prakashsoftware.agent.outbox.v1';
configureBus({
  serverUrl: SERVER_URL,
  authProvider: () => ({
    kind: 'agent',
    jwt: useAgentStore.getState().tokens?.accessToken ?? '',
  }),
  outboxStorage: {
    load: () => JSON.parse(localStorage.getItem(OUTBOX_KEY) ?? '[]') as BusEvent[],
    save: (events) =>
      events.length
        ? localStorage.setItem(OUTBOX_KEY, JSON.stringify(events))
        : localStorage.removeItem(OUTBOX_KEY),
  },
});
configureWebRTCMetrics(SERVER_URL);

// Seed mock visitors + other agents at boot so the dashboard isn't empty.
seedFixtures();
// Wire mock-state changes into the notifications store. Subscribe AFTER seeding
// so fixture rows don't each generate a toast.
installEventBridge();
// Subscribe to the shared event bus — real widgets feed into the same
// stores from this point on.
installAgentBusBridge();
// AI fallback: when a visitor talks and no human is engaged, the orchestrator
// proxies through the configured LLM provider and replies on the same bus.
// AI orchestrator is server-side now (Session 5.8). Nothing to install here.
// PWA: register SW (prod only) + listen for install prompt.
registerServiceWorker();
listenForInstallPrompt();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

createRoot(rootEl).render(
  <StrictMode>
    <App />
    <ToastDeck />
    <ConfirmDialog />
    <InstallBanner />
    <DevPanel />
  </StrictMode>,
);
